<template>
  <!-- Edit server: move a legacy X-Proxy server onto a ProProxyUI box (mode "move"), or undo that move (mode "undo") -->
  <div class="float-left w-full px-2 pb-2 modal-item">
    <div v-if="mode === 'move'" class="cv">
      <div class="cv-head"><b>Move this server to a ProProxyUI box</b></div>
      <p class="cv-dim">Customers keep the same host, HTTP port, login, licence and rotation link. Put the ProProxyUI box's address in Domain and its API token above.</p>

      <div class="cv-step">
        <span class="cv-n">1</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Modem list from the X-Proxy box</span>
            <button type="button" class="cv-btn" :disabled="busy" @click="saveSnapshot">{{ st.snapshot_at ? 'Save again' : 'Save modem list' }}</button>
          </div>
          <small v-if="st.snapshot_at" class="cv-ok">Saved {{ when(st.snapshot_at) }} · {{ st.positions }} positions</small>
          <small v-else class="cv-warn">Not saved yet. Save it while the X-Proxy box is still online: it records which modem is on each position.</small>
        </div>
      </div>

      <div class="cv-step">
        <span class="cv-n">2</span>
        <div class="cv-body">
          <label class="cv-line cv-tick"><input type="checkbox" v-model="dualPort" class="form-check-input shadow-none" /> XProxy 2-port mode (SOCKS5 = HTTP + 1000, as now)</label>
          <div class="cv-line">
            <span>Check every port against the new box</span>
            <button type="button" class="cv-btn" :disabled="busy || !st.snapshot_at || !domain || !token" @click="runPreview">Preview</button>
          </div>
          <div v-if="preview" class="cv-table">
            <div class="cv-row cv-row--head"><span>HTTP</span><span>SOCKS5</span><span>Modem</span><span>Login</span><span>Licence</span><span></span></div>
            <div v-for="l in preview.lines" :key="l.http_port" class="cv-row">
              <span class="cv-mono">{{ l.http_port }}</span>
              <span class="cv-mono" :class="{ 'cv-warn': l.socks_changes }">{{ l.socks_after }}<small v-if="l.socks_changes"> (was {{ l.socks_port }})</small></span>
              <span>{{ l.modem || (l.imei ? '…' + l.imei.slice(-4) : '—') }}<small v-if="l.modem_state"> · {{ l.modem_state }}</small></span>
              <span class="cv-mono">{{ l.login || (l.ips ? 'IP ' + l.ips : '—') }}<small class="cv-dim" style="display:block">{{ l.daily_gb ? l.daily_gb + ' GB/day' : 'no daily limit' }} · {{ l.max_conns ? 'max ' + l.max_conns + ' conns' : 'no conn limit' }} · {{ l.limit_mbps ? l.limit_mbps + ' Mbps' : 'no speed limit' }}</small></span>
              <span>{{ l.licence ? (l.licence.email || '') + ' · ' + l.licence.status.toLowerCase() : (l.sold ? 'sold' : 'for sale') }}</span>
              <span :class="l.problems.length ? 'cv-bad' : (l.notes && l.notes.length) ? 'cv-note' : 'cv-ok'">{{ l.problems.length ? l.problems.join('; ') : (l.notes && l.notes.length) ? l.notes.join('; ') : 'OK' }}</span>
            </div>
            <small class="cv-dim">Box {{ preview.box_version ? 'v' + preview.box_version : '' }} · {{ preview.lines.length }} ports · {{ preview.issues ? preview.issues + ' with a problem' : 'all ready' }}</small>
          </div>
        </div>
      </div>

      <div class="cv-step">
        <span class="cv-n">3</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Create the ports on the box and switch the server over</span>
            <button type="button" class="cv-btn cv-btn--pri" :disabled="busy || !preview || !preview.ok" @click="convert">{{ busy ? 'Working…' : 'Move server' }}</button>
          </div>
          <small class="cv-dim">Can be undone here for 7 days (put the X-Proxy box back first).</small>
        </div>
      </div>
    </div>

    <div v-else-if="mode === 'undo' && st.can_undo" class="cv">
      <div class="cv-line">
        <span>Moved from {{ st.source === 'proxysmart' ? 'ProxySmart' : 'X-Proxy' }} {{ when(st.converted_at) }}{{ st.pair_name ? ` with ${st.pair_name}` : '' }}. Undo until {{ when(st.undo_until) }}.</span>
        <button type="button" class="cv-btn" :disabled="busy" @click="undo">{{ busy ? 'Working…' : 'Undo the move' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '@/stores';

const props = defineProps({ mode: { type: String, default: 'move' }, serverUuid: String, domain: String, token: String });
const emit = defineEmits(['done']);
const API = `${process.env.VUE_APP_VUE_API_URL}/admin/proproxyui/migrate`;
const alerts = useAlertStore();
const st = ref({});
const busy = ref(false);
const dualPort = ref(true);
const preview = ref(null);
const when = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const load = async () => {
  if (!props.serverUuid) return;
  try { st.value = await fetchWrapper.post(`${API}/status`, { server_uuid: props.serverUuid }); } catch (e) { st.value = {}; }
};
watch(() => props.serverUuid, () => { preview.value = null; load(); }, { immediate: true });
watch(() => [props.domain, props.token, dualPort.value], () => { preview.value = null; });

const saveSnapshot = async () => {
  busy.value = true;
  try { const r = await fetchWrapper.post(`${API}/snapshot`, { server_uuid: props.serverUuid }); alerts.success(`Modem list saved: ${r.positions} positions, ${r.with_modem} with a modem`); await load(); }
  catch (e) { alerts.error(e); }
  busy.value = false;
};
const runPreview = async () => {
  busy.value = true;
  try { preview.value = await fetchWrapper.post(`${API}/preview`, { server_uuid: props.serverUuid, domain: props.domain, token: props.token, dual_port: dualPort.value }); }
  catch (e) { preview.value = null; alerts.error(e); }
  busy.value = false;
};
const convert = async () => {
  if (!confirm(`Move this server to the ProProxyUI box now? ${preview.value.lines.length} ports are created on the box and every licence moves with them.`)) return;
  busy.value = true;
  try {
    const r = await fetchWrapper.post(`${API}/convert`, { server_uuid: props.serverUuid, domain: props.domain, token: props.token, dual_port: dualPort.value });
    alerts.success(`Moved: ${r.ports} ports, ${r.licences} licences`);
    emit('done');
  } catch (e) { alerts.error(e); }
  busy.value = false;
};
const undo = async () => {
  const from = st.value.source === 'proxysmart' ? 'ProxySmart' : 'X-Proxy';
  if (!confirm(`Undo the move? The server${st.value.pair_name ? ` and ${st.value.pair_name}` : ''} go${st.value.pair_name ? '' : 'es'} back to ${from} (ports, licences and packages as before) and the ports are removed from the ProProxyUI box. The ${from} box must be back in place.`)) return;
  busy.value = true;
  try { const r = await fetchWrapper.post(`${API}/undo`, { server_uuid: props.serverUuid }); alerts.success(`Undone: ${r.licences} licences back on ${from}`); emit('done'); }
  catch (e) { alerts.error(e); }
  busy.value = false;
};
</script>

<style scoped>
.cv { border: 1px solid var(--ui-line); border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; gap: 10px; }
.cv-head { font-size: 15px; }
.cv-dim { color: var(--ui-dim); font-size: 12px; margin: 0; }
.cv-step { display: flex; gap: 10px; align-items: flex-start; }
.cv-n { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--ui-line-strong); display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.cv-body { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cv-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 13px; }
.cv-tick { justify-content: flex-start; gap: 8px; }
.cv-btn { padding: 3px 12px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 12px; background: transparent; color: inherit; cursor: pointer; white-space: nowrap; }
.cv-btn:disabled { opacity: 0.5; cursor: default; }
.cv-btn--pri { background: var(--ui-pri); border-color: var(--ui-pri); color: #ffffff; }
.cv-ok { color: #22c55e; font-size: 12px; }
.cv-warn { color: #f59e0b; font-size: 12px; }
.cv-bad { color: #ef4444; font-size: 12px; }
.cv-note { color: #f59e0b; font-size: 12px; }
.cv-table { display: flex; flex-direction: column; border: 1px solid var(--ui-line); border-radius: 8px; overflow-x: auto; margin-top: 4px; }
.cv-row { display: grid; grid-template-columns: 60px 90px minmax(90px, 1fr) minmax(110px, 1fr) minmax(120px, 1.2fr) minmax(90px, 1fr); gap: 8px; padding: 5px 8px; border-top: 1px solid var(--ui-line); font-size: 12px; min-width: 620px; }
.cv-row--head { border-top: 0; color: var(--ui-dim); }
.cv-mono { font-family: ui-monospace, Menlo, Consolas, monospace; }
</style>
